import { useTheme } from '@emotion/react';
import { Formik } from 'formik';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useEffect, useRef } from 'react';

import { Alert, Box, Button, EntryList, Flex, Header, Text, TextField } from '~/components/blocks';
import { PasswordRuleChecklist } from '~/components/partials/PasswordRuleChecklist';
import { loginState, loginWithNewPasswordState } from '~/state/login/atoms';
import { DEFAULT_PASSWORD_POLICY, passwordPlaceholder } from '~/utils/password';

import { useDefaultValues } from './use-default_values';
import { useSigninPractitionerWithCode } from './use-signin-practitioner_with_code';
import { validationSchema } from './validation';

export const NewPassword = () => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const defaultValues = useDefaultValues();

  const { loading, error, handleSubmit } = useSigninPractitionerWithCode();

  const resetState = useResetAtom(loginWithNewPasswordState);
  const { passwordPolicy } = useAtomValue(loginState);
  const policy = passwordPolicy ?? DEFAULT_PASSWORD_POLICY;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.select();
    }
  }, []);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return (
    <Box>
      <Formik
        initialValues={defaultValues}
        validationSchema={validationSchema(policy)}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <>
            <Header attached="box">パスワード設定</Header>
            {error && (
              <Alert status="error" mb={theme.space.xl} whiteSpace="pre-wrap">
                {error.responseData && error.responseData['errors']
                  ? error.responseData['errors']
                  : 'エラーが発生しました'}
              </Alert>
            )}
            <form onSubmit={formik.handleSubmit}>
              <Box>
                <Text mb={theme.space.xl}>
                  仮パスワードでの認証ができました。
                  <br />
                  あらたにパスワードを設定してログインしてください。
                </Text>
                <EntryList>
                  <EntryList.Head>パスワード</EntryList.Head>
                  <EntryList.Body>
                    <TextField
                      ref={inputRef}
                      disabled={loading}
                      name="password"
                      type="password"
                      error={!!formik.errors.password}
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      placeholder={passwordPlaceholder(policy)}
                    />
                    {passwordPolicy && (
                      <Box marginTop={theme.space.s}>
                        <PasswordRuleChecklist password={formik.values.password} policy={policy} />
                      </Box>
                    )}
                  </EntryList.Body>
                </EntryList>
                <EntryList>
                  <EntryList.Head>パスワード（確認）</EntryList.Head>
                  <EntryList.Body>
                    <TextField
                      disabled={loading}
                      type="password"
                      name="passwordConfirmation"
                      error={formik.errors.passwordConfirmation}
                      value={formik.values.passwordConfirmation}
                      onChange={formik.handleChange}
                      placeholder={passwordPlaceholder(policy)}
                    />
                  </EntryList.Body>
                </EntryList>
              </Box>
              <Flex justifyContent="flex-end" flexWrap="wrap" mt={theme.space.xl}>
                <Button use="base" type="submit" loading={loading}>
                  ログイン
                </Button>
              </Flex>
            </form>
          </>
        )}
      </Formik>
    </Box>
  );
};
